"use client";

import { joinClassNames } from "@utils/helpers";
import { usePrevious } from "@utils/hooks";
import Link from "next/link";
import { useState } from "react";
import { motion } from "framer-motion";
import { SparklesIcon } from "@heroicons/react/20/solid";
import { sub } from "date-fns";

interface Submenu {
  name: string;
  slug: string;
  isNew?: boolean;
}

interface Props {
  submenus: Submenu[];
  current: Submenu;
  hrefPrefix?: string;
}

/**
 * The submenu component is mainly used for navigation on a route segment. Example, in the account page has a route of "/account", in account page
 * route segments such as "/order-history", "/my-tickets" etc. This just gives users the ability to navigate these route segments with ease.
 *
 */
export default function Submenu({ submenus, current, hrefPrefix = "" }: Props) {
  const [hoveredSubmenu, setHoveredSubmenu] = useState<{
    height: number;
    y: number;
  } | null>(null);
  const lastHovered = usePrevious(hoveredSubmenu);

  return (
    <div className="relative flex flex-1 flex-col">
      {submenus.map((submenu, index) => (
        <Link
          key={submenu.name}
          id={submenu.name}
          href={`${hrefPrefix}${submenu.slug}`}
          onMouseEnter={() => {
            const height =
              document.getElementById(submenu.name)?.getBoundingClientRect()
                .height ?? 0;
            let y = 0;
            for (let i = 0; i < index; ++i) {
              y +=
                document
                  .getElementById(submenus[i].name)
                  ?.getBoundingClientRect().height ?? 0;
            }
            setHoveredSubmenu({ height, y });
          }}
          onMouseLeave={() => setHoveredSubmenu(null)}
          className={joinClassNames(
            "flex items-center justify-between relative z-10 px-4 py-2 font-medium hover:text-indigo-600 transition-colors ease-in-out duration-200",
            current.slug === submenu.slug ? "text-indigo-500" : "text-gray-400"
          )}
        >
          {submenu.name}
          {submenu.isNew && (
            <span className="inline-flex items-center gap-1 rounded-lg px-1 py-0.5 text-sm font-semibold bg-indigo-100 max-h-6">
              <SparklesIcon className="w-4 h-4 text-indigo-800 animate-shimmer" />
              <span className="text-indigo-800">
                {"New".split("").map((char, index) => (
                  <span
                    key={index}
                    className="animate-shimmer"
                    style={{ animationDelay: `${50 * (index + 1)}ms` }}
                  >
                    {char}
                  </span>
                ))}
              </span>
            </span>
          )}
        </Link>
      ))}
      <motion.span
        animate={
          hoveredSubmenu
            ? { height: hoveredSubmenu.height, y: hoveredSubmenu.y, opacity: 1 }
            : {
                height: lastHovered?.height,
                y: lastHovered?.y,
                opacity: 0,
                transition: { opacity: { duration: 0.5 } },
              }
        }
        transition={{ ease: "easeInOut", duration: 0.2 }}
        className="absolute w-full rounded-lg top-0 bg-indigo-50 pointer-events-none"
      />
    </div>
  );
}
